import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: LucideIcon;
  title: string;
  description?: string;
  /** Optional call-to-action (e.g. a Button) rendered under the copy. */
  action?: React.ReactNode;
}

/**
 * Clay empty state — soft raised icon badge, title, optional description + CTA.
 * Replaces the bespoke inline empty blocks the surfaces grew independently.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
  ...props
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-3 rounded-card border-2 border-brand-outline-variant/20 bg-card px-6 py-12 text-center shadow-clay-sm',
        className,
      )}
      {...props}
    >
      {Icon && (
        <div className="flex h-14 w-14 items-center justify-center rounded-button bg-muted text-muted-foreground shadow-clay-sm">
          <Icon className="h-7 w-7" />
        </div>
      )}
      <p className="font-heading text-lg font-bold">{title}</p>
      {description && <p className="max-w-sm text-sm text-muted-foreground">{description}</p>}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}
